import { Color3, Color4, Constants, ParticleSystem, StandardMaterial, Texture, type Scene } from '@babylonjs/core';
import { getSceneTexture } from '../scene-textures';

/**
 * Kenney's Particle Pack (CC0; docs/asset-credits.md), as the runtime copies
 * `build-kenney-particles.mts` makes: gray shading in the color and coverage
 * in alpha, so a tint multiplied in keeps a puff's billows and a scorch's
 * cracks (`star_08` alone is pure white). 256 px for the sprites drawn
 * large, 128 px for the rest.
 */
export const PARTICLE_SPRITE_FAMILIES = {
    circle: 5,
    dirt: 3,
    fire: 2,
    flame: 6,
    flare: 1,
    light: 3,
    magic: 5,
    muzzle: 5,
    scorch: 3,
    scratch: 1,
    slash: 4,
    smoke: 10,
    spark: 7,
    star: 9,
    symbol: 2,
    trace: 7,
    twirl: 3,
    window: 4,
} as const;

export type ParticleSpriteFamily = keyof typeof PARTICLE_SPRITE_FAMILIES;

/** One sprite: its family and its number in it, from 1 (`smoke_04` is `{ family: 'smoke', index: 4 }`). */
export interface ParticleSprite {
    readonly family: ParticleSpriteFamily;
    readonly index: number;
}

/** The sprite's file name, without its extension: `smoke_04`. */
export function particleSpriteName(sprite: ParticleSprite): string {
    return `${sprite.family}_${String(sprite.index).padStart(2, '0')}`;
}

export function buildParticleSpriteUrl(sprite: ParticleSprite): string {
    return `/assets/vfx/particles/${particleSpriteName(sprite)}.png`;
}

/** Every sprite in a family, in file order. */
export function particleSpritesOf(family: ParticleSpriteFamily): readonly ParticleSprite[] {
    return Array.from({ length: PARTICLE_SPRITE_FAMILIES[family] }, (_, offset) => ({ family, index: offset + 1 }));
}

/**
 * A sprite's texture, loaded once per scene and shared by everything that
 * draws it, so it is never disposed by a user: it goes with the scene.
 */
export function getParticleSprite(scene: Scene, sprite: ParticleSprite): Texture {
    const url = buildParticleSpriteUrl(sprite);
    return getSceneTexture(scene, url, () => {
        const texture = new Texture(url, scene);
        texture.hasAlpha = true;
        texture.wrapU = Texture.CLAMP_ADDRESSMODE;
        texture.wrapV = Texture.CLAMP_ADDRESSMODE;
        return texture;
    });
}

/**
 * An unlit material drawing `sprite` on a quad, tinted `color` (its gray
 * shading kept), added as light or blended over what is behind it. Fade it
 * with `alpha`. A plain `dispose()` leaves the shared sprite alone.
 */
export function createSpriteMaterial(scene: Scene, name: string, sprite: ParticleSprite, color: Color3, additive: boolean): StandardMaterial {
    const material = new StandardMaterial(name, scene);
    const texture = getParticleSprite(scene, sprite);
    material.disableLighting = true;
    material.diffuseColor = Color3.Black();
    material.specularColor = Color3.Black();
    material.emissiveTexture = texture;
    material.emissiveColor = color.clone();
    material.opacityTexture = texture;
    material.backFaceCulling = false;
    material.disableDepthWrite = true;
    material.alphaMode = additive ? Constants.ALPHA_ADD : Constants.ALPHA_COMBINE;
    return material;
}

/** How a particle system draws its sprites. */
export interface ParticleSpriteLook {
    /** The system draws one of these, picked as it is made, so repeated bursts differ. */
    readonly sprites: readonly ParticleSprite[];
    /** Each particle starts at a random angle and turns up to this fast either way, in radians a second. */
    readonly spin?: number;
    /** Each particle is drawn along its motion, this many times as long as it is wide (sparks as streaks). */
    readonly stretch?: number;
}

const sprites = (family: ParticleSpriteFamily, ...indices: readonly number[]): readonly ParticleSprite[] =>
    indices.map((index) => ({ family, index }));

/** Dust kicked along the floor: soft, billowing puffs. */
export const DUST_LOOK: ParticleSpriteLook = { sprites: sprites('smoke', 4, 5, 7), spin: 0.6 };
/** Smoke trailing or rolling up off something: denser, lumpier puffs. */
export const SMOKE_LOOK: ParticleSpriteLook = { sprites: sprites('smoke', 7, 8), spin: 0.8 };
/** Chips of stone and grit thrown up: specks, not soft discs. */
export const DEBRIS_LOOK: ParticleSpriteLook = { sprites: sprites('dirt', 1, 2), spin: 4 };
/** Sparks: thin streaks drawn along their flight. */
export const SPARK_LOOK: ParticleSpriteLook = { sprites: sprites('trace', 1, 2), stretch: 3.5 };
/** Flames: upright tongues of fire, never turned. */
export const FLAME_LOOK: ParticleSpriteLook = { sprites: sprites('muzzle', 2, 3, 4, 5) };
/** A hit's fiery flare: a ragged burst. */
export const BURST_LOOK: ParticleSpriteLook = { sprites: sprites('fire', 1, 2), spin: 1.5 };
/** Crackles of lightning: tangled arcs, turned at random. */
export const CRACKLE_LOOK: ParticleSpriteLook = { sprites: sprites('spark', 1, 2, 3, 4), spin: 6 };
/** Glints of essence: the sharp four-point star, tinted (an essence bow's shimmer, its arrow's trail, their burst). */
export const ESSENCE_GLINT_LOOK: ParticleSpriteLook = { sprites: sprites('star', 6), spin: 2 };
/** The streak an essence arrow is drawn as, stretched along its flight. */
export const ESSENCE_STREAK_SPRITE: ParticleSprite = { family: 'trace', index: 6 };
/** The tall crackles a lightning bolt is drawn from, laid end to end along it. */
export const LIGHTNING_RUN_SPRITES: readonly ParticleSprite[] = sprites('spark', 5, 6);

/**
 * Draws `system`'s particles with one of `look`'s sprites in place of the
 * soft glow disc. The sprite is shared: dispose the system with
 * `dispose(false)`, never with its texture.
 */
export function applyParticleSprite(system: ParticleSystem, scene: Scene, look: ParticleSpriteLook): void {
    // Which of the look's sprites a burst draws is only a visual: any randomness will do.
    const sprite = look.sprites[Math.floor(Math.random() * look.sprites.length)] ?? look.sprites[0];
    if (sprite) {
        system.particleTexture = getParticleSprite(scene, sprite);
    }
    if (look.spin !== undefined) {
        system.minInitialRotation = 0;
        system.maxInitialRotation = Math.PI * 2;
        system.minAngularSpeed = -look.spin;
        system.maxAngularSpeed = look.spin;
    }
    if (look.stretch !== undefined) {
        // Stretched particles lie along their direction, with the sprite's height along it.
        system.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;
        system.minScaleY = look.stretch;
        system.maxScaleY = look.stretch;
    }
}

const LOOKS: readonly ParticleSpriteLook[] = [DUST_LOOK, SMOKE_LOOK, DEBRIS_LOOK, SPARK_LOOK, FLAME_LOOK, BURST_LOOK, CRACKLE_LOOK, ESSENCE_GLINT_LOOK];

/**
 * Starts loading every look's sprites, and the lightning bolt's, so whichever
 * one a burst picks is ready: a warmed system shows only the one it drew.
 */
export function preloadParticleSpriteLooks(scene: Scene): void {
    for (const look of LOOKS) {
        look.sprites.forEach((sprite) => getParticleSprite(scene, sprite));
    }
    LIGHTNING_RUN_SPRITES.forEach((sprite) => getParticleSprite(scene, sprite));
    getParticleSprite(scene, ESSENCE_STREAK_SPRITE);
}

/**
 * Tints particles `color` (#325): born `whiteShare` of the way to white,
 * then the color itself, dying to a dark, clear trace of it.
 */
export function applyParticleTint(system: ParticleSystem, color: Color3, whiteShare: number): void {
    const light = Color3.Lerp(color, Color3.White(), whiteShare);
    system.color1 = new Color4(light.r, light.g, light.b, 1);
    system.color2 = new Color4(color.r, color.g, color.b, 1);
    system.colorDead = new Color4(color.r * 0.3, color.g * 0.3, color.b * 0.3, 0);
}

/**
 * Burning flames: white-hot at birth, through orange to a dim red as each
 * flame dies, drawn additively (Immolate's flames and its burning ground).
 */
export function applyFlameRamp(system: ParticleSystem): void {
    system.addColorGradient(0, new Color4(1, 0.92, 0.7, 1));
    system.addColorGradient(0.35, new Color4(1, 0.55, 0.12, 0.9));
    system.addColorGradient(1, new Color4(0.6, 0.08, 0.02, 0));
    system.blendMode = ParticleSystem.BLENDMODE_ADD;
}
